/**
 * **周期规则**对话框（批次 `95` 建 · 批次 `128` 加**编辑态** · 批次 `132` 加**周期记账模板**）
 *
 * ## 🔴 为什么现在才做
 *
 * 计划页第 ③ 段一直只显示"规则列表 + 启停"，空态文案还写着
 * 「给「待缴 / 待摊」账户绑一条规则后，每期会自动生成流水」——
 * 但**没有任何地方能创建规则**（`AccountForm` 里连周期字段都没有）。
 * 负责人 2026-10-22：「有没有周期记账，入口在哪里」。⇒ 入口在这里。
 *
 * ## 🆕 批 `128`：编辑态（负责人 2026-10-31：「周期记账需要可以编辑」）
 *
 * 同一个对话框承担「建」与「改」两份职责，理由 = **不造两个半成品表单**
 * （`2026-10-07-f-schedule.md` §4 的原话：「服务端 CRUD 已具备，但端侧表单与 F1 的编辑态应**一次做完**」）。
 *
 * ## 🆕 批 `132`：新增「周期记账模板」（`DR-074`）
 *
 * v1 迁来的 6 条规则（每月 1 号扣卡、每月 9 号公积金到账）在 v2 **没有一种形态能承载**，
 * 已于 2026-11-01 在生产全部停用。模板 = **到期直接记一笔已发生的收支**（`settled`、计入收支、
 * 无需销账），挂在"钱真的进出的那个账户"上，方向由金额符号表达。
 *
 * 🔴 **账户候选随 `kind` 变**（唯一判据在 `features/schedule-candidates.ts`）：
 * `recurring` ⇒ 待缴账户 · `amortization` ⇒ 待摊账户 · `loan` ⇒ 贷款账户 ·
 * `template` ⇒ **全部账户**（服务端对模板不判品类，端侧也不先替它拒，`DR-074` D3）。
 *
 * ## 🔴 编辑态锁什么、不锁什么（依据 `contract/schedule.ts` 的 `UpdateScheduleRequest`）
 * | 字段 | 可改？ | 理由 |
 * |---|---|---|
 * | 账户 | ❌ | 账户 : 规则 = **1:1**（`template` 虽是 1:N，但"改绑 = 换规则"同样成立；服务端契约里没有 `accountId`） |
 * | 种类 `kind` | ❌ | 与账户形态挂钩（待摊 ↔ 摊销）；换了必然被内核拒 |
 * | 金额 / 频率 / 下次到期 / 分类 / 名称 | ✅ | `UpdateScheduleRequest` 的全部字段 |
 * | `enabled` | ❌ 不在此处 | **停用是列表行上的开关**，不进表单（否则两个写入口抢同一个字段） |
 *
 * ## 🔴 判定不在这里
 *
 * 「账户与种类是否配套」（待摊 ↔ `amortization`、待缴 ↔ `recurring`、贷款 ↔ `loan`；
 * **模板无要求**）由**服务端内核**判（`checkScheduleShape`），本对话框只做：
 * ① 把用户的选择拼成请求体；② 把服务端的报错**原样**展示（不编第二份文案）。
 */

import { useState, type ReactElement } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ScheduleFrequency,
  ScheduleKind,
  centsToYuanText,
  currencySymbolOf,
  toCents,
  type AccountView,
  type CategoryView,
  type CreateScheduleRequest,
  type ScheduleView,
  type UpdateScheduleRequest,
} from '@jiaying/shared';
import { createSchedule, updateSchedule } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { localToday } from '../app/local-day.js';
import { yuanTextToCentsOrNull } from '../features/txn-draft.js';
import { AccountPicker } from './AccountPicker.js';
import { CategoryPicker } from './CategoryPicker.js';
import { SCHEDULE_KIND_LABELS } from '../features/plan-labels.js';
import { accountCandidatesFor, defaultKindFor } from '../features/schedule-candidates.js';

const KIND_OPTIONS: readonly { value: ScheduleKind; label: string }[] = [
  { value: ScheduleKind.Recurring, label: '定期收支（待缴 / 固定支出）' },
  { value: ScheduleKind.Template, label: '周期记账模板（固定扣款 / 固定入账）' },
  { value: ScheduleKind.Amortization, label: '待摊摊销（一次付、按月摊）' },
  { value: ScheduleKind.Installment, label: '分期' },
  { value: ScheduleKind.Loan, label: '贷款还款' },
];

const FREQ_OPTIONS: readonly { value: ScheduleFrequency; label: string }[] = [
  { value: ScheduleFrequency.Monthly, label: '每月' },
  { value: ScheduleFrequency.Weekly, label: '每周' },
  { value: ScheduleFrequency.Quarterly, label: '每季' },
  { value: ScheduleFrequency.Yearly, label: '每年' },
  { value: ScheduleFrequency.Daily, label: '每天' },
];

export interface ScheduleDialogProps {
  /**
   * 本账本**全部**账户。
   *
   * 🔴 批 `132` 起由调用方传全量（不再预筛成"内部挂账 / 贷款"）——
   * 筛选按 `kind` 分派，唯一判据在 `features/schedule-candidates.ts`。
   * 预筛 + 分派两处各筛一次就会出现「模板选择器里没有储蓄卡」这种**看着像缺陷、其实是筛没了**的现象。
   */
  accounts: readonly AccountView[];
  categories: readonly CategoryView[];
  onClose: () => void;
  onCreated: (view: ScheduleView) => void;
  /**
   * 🆕 批 `128`：传了就是**编辑态**，不传就是新建态。
   *
   * ⚠️ 调用方必须给编辑器加 `key={rule.id}` —— 否则同一个组件实例从 A 规则切到 B 规则时
   * `useState` 的初值不会重跑，表单会显示上一条规则的值（最坏的一种：改错了规则还以为在改 B）。
   */
  rule?: ScheduleView | null;
}

export function ScheduleDialog({
  accounts,
  categories,
  onClose,
  onCreated,
  rule,
}: ScheduleDialogProps): ReactElement {
  const qc = useQueryClient();
  const editing = rule !== null && rule !== undefined;

  const first = accounts[0];
  /**
   * 编辑态：账户与种类**只读**（见文件头的锁定表）。
   * 🔴 用 `rule.accountId` 而非 `first` —— 编辑一条挂在待摊账户上的规则时，
   * 若误取 `accounts[0]` 的币种，金额符号会跟着错。
   */
  const [accountId, setAccountId] = useState(editing ? (rule.accountId ?? '') : (first?.id ?? ''));
  /**
   * 默认种类**按账户品类推断**（唯一判据 `defaultKindFor`：待摊 ⇒ 摊销 · 待缴 ⇒ 周期账 ·
   * 其余实账户 ⇒ 周期记账模板）。
   * 🔴 只是**默认值**，用户可改；最终是否配套由服务端判。
   */
  const [kind, setKind] = useState<ScheduleKind>(editing ? rule.kind : defaultKindFor(first));
  const [frequency, setFrequency] = useState<ScheduleFrequency>(
    editing ? rule.frequency : ScheduleFrequency.Monthly,
  );
  /**
   * 🔴 金额预填 = **原样搬服务端的 `amount`**（保留符号）——
   * 负号表示方向为 `in`（预存待缴账户），`centsToYuanText` 自己带符号，端侧不判方向。
   */
  const [amountText, setAmountText] = useState(
    editing ? centsToYuanText(toCents(rule.amount)) : '',
  );
  const [categoryId, setCategoryId] = useState<string | null>(editing ? rule.categoryId : null);
  /**
   * `nextDueDay = null` ⇒ 规则**没有下一期**（契约明文）。此时日期框留空，
   * 提交时**不传** `nextRunAt`（`UpdateScheduleRequest` 里它是 `DayString.optional()`，
   * **不能置 null** —— 想"没有下一期"只能靠停用，不能靠清空日期）。
   */
  const [nextRunAt, setNextRunAt] = useState(
    editing ? (rule.nextDueDay ?? '') : localToday(),
  );
  const [name, setName] = useState(editing ? (rule.name ?? '') : '');
  const [error, setError] = useState<string | null>(null);

  const account = accounts.find((a) => a.id === accountId);
  /**
   * 编辑态下账户可能在候选之外（历史规则可能挂在已删除 / 别的账户上）
   * ⇒ 币种回退用 `rule.currency`，符号才不至于空。
   */
  const currency = account?.currency ?? (editing ? rule.currency : null);
  const sym = currency === null ? '' : currencySymbolOf(currency);
  const cents = yuanTextToCentsOrNull(amountText, { allowNegative: true });
  const amountBad = amountText.trim() === '' || cents === null || (cents as number) === 0;

  /**
   * 当前 `kind` 能挂的账户（🔴 唯一判据 `accountCandidatesFor`，本组件不自己 `filter`）。
   * 编辑态下账户只读，候选只用于「币种 / 空态」判断，不渲染选择器。
   */
  const candidates = accountCandidatesFor(kind, accounts);

  /**
   * 换 `kind`：候选集合变了 ⇒ 当前账户若不在新集合里，**连带换到第一个合法候选**。
   * 🔴 不换的话提交必然被服务端拒（而用户看到的是"我什么都没动，怎么报错了"）。
   */
  const pickKind = (nextKind: ScheduleKind): void => {
    setKind(nextKind);
    const next = accountCandidatesFor(nextKind, accounts);
    if (!next.some((a) => a.id === accountId)) setAccountId(next[0]?.id ?? '');
    setError(null);
  };

  const invalidate = (): void => {
    // 改规则/改启停都会改变"日历里有没有这一项" ⇒ 两个键都要失效（写路径影响读路径）
    void qc.invalidateQueries({ queryKey: ['schedules'] });
    void qc.invalidateQueries({ queryKey: ['upcoming'] });
  };

  const create = useMutation({
    mutationFn: (json: CreateScheduleRequest) => createSchedule(json),
    onSuccess: (view) => {
      invalidate();
      onCreated(view);
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const update = useMutation({
    mutationFn: (json: UpdateScheduleRequest) => updateSchedule((rule as ScheduleView).id, json),
    onSuccess: (view) => {
      invalidate();
      onCreated(view);
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const busy = create.isPending || update.isPending;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" data-testid="schedule-dialog">
      <DialogTitle>{editing ? '编辑周期规则' : '新建周期规则'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {editing
              ? '只影响**未来**期次：已经生成的流水金额与日期一个都不会改（AC-F1.8）。'
              : kind === ScheduleKind.Template
                ? '模板到期会直接记一笔「已经发生」的收支（不是待缴，不用销账）；挂在钱真的进出的那个账户上，支出记正数、入账记负数。'
                : '规则挂在一个**待缴 / 待摊**账户上，每期自动生成流水；已生成的流水**不会被后来的改规则影响**。'}
          </Typography>

          {candidates.length === 0 && !editing ? (
            <Alert severity="info" data-testid="schedule-no-candidate">
              {kind === ScheduleKind.Installment
                ? '信用卡分期延后到 P1，本版不支持。'
                : '还没有这个类型能用的账户。先在「账户」页建一个（待缴 / 待摊规则需要「内部挂账」账户；周期记账模板可挂任意实账户）。'}
            </Alert>
          ) : (
            <>
              {editing ? (
                /* 编辑态：账户与种类**只读展示**（1:1 从属 + 品类配套，见文件头锁定表） */
                <Alert severity="info" data-testid="schedule-locked-fields">
                  账户与规则类型不能改（规则跟着账户走，换就删旧建新）：
                  <b>{rule.accountName ?? rule.accountId ?? '（账户已删除）'}</b> ·{' '}
                  {SCHEDULE_KIND_LABELS[rule.kind]}
                </Alert>
              ) : (
                <>
                  {/*
                    🆕 批 `115`（T4）：**换控件不换候选** —— 候选仍是页面传入的 `accounts`（顺序原样）。
                    🆕 批 `132`：候选**按 `kind` 分派**（`accountCandidatesFor`）；模板放开到任意实账户。
                    🔴 label 保留「名称 · 品类」；`matchText` 仍只取名称（`AccountPicker` 内部保证，
                    品类/归属人不进匹配串 —— §5.8.4）。
                  */}
                  <AccountPicker
                    size="small"
                    label="挂在哪个账户"
                    accounts={candidates.map((a) => ({
                      id: a.id,
                      name: a.name,
                      label:
                        a.categoryLabel === null ? a.name : `${a.name} · ${a.categoryLabel}`,
                    }))}
                    value={accountId}
                    onChange={(id) => {
                      setAccountId(id);
                      const a = accounts.find((x) => x.id === id);
                      // 当前 kind 挂不上这个账户 ⇒ 切到该账户的默认 kind（否则提交必然 400）
                      if (a !== undefined && accountCandidatesFor(kind, [a]).length === 0) {
                        setKind(defaultKindFor(a));
                      }
                      setError(null);
                    }}
                    testId="schedule-account"
                  />

                  <TextField
                    select
                    size="small"
                    label="规则类型"
                    value={kind}
                    onChange={(e) => {
                      pickKind(e.target.value as ScheduleKind);
                    }}
                    helperText={
                      kind === ScheduleKind.Template
                        ? '到期直接记一笔已发生的收支（不产生待缴、不用销账）；可挂任意实账户，支出记正数、入账记负数'
                        : '要与账户品类配套（待摊 ↔ 摊销）；不配套时服务端会明确拒绝'
                    }
                    slotProps={{ htmlInput: { 'data-testid': 'schedule-kind' } }}
                  >
                    {KIND_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              )}

              {/*
                🔴 批 `131`：**未绑账户的规则**（v1 迁移来的 / 账户被物理删除）。
                它不生成任何流水（引擎见 `accountId = NULL` 直接跳过）——
                用户能改金额，但改完看不到新流水 ⇒ 必须当场说清，否则又是一轮「改了没反应」。
              */}
              {editing && rule.accountName === null ? (
                <Alert severity="warning" data-testid="schedule-orphan-rule">
                  这条规则<b>没有绑定账户</b>（历史数据或账户已被删除），因此<b>不会生成任何流水</b>。
                  改金额 / 频率只改这条规则本身；要让它重新出账，请删除后<b>重建一条</b>
                  （待缴 / 待摊规则要选「内部挂账」账户，周期记账模板可挂任意实账户）。
                </Alert>
              ) : null}

              <Stack direction="row" spacing={1.5}>
                <TextField
                  select
                  size="small"
                  label="频率"
                  value={frequency}
                  onChange={(e) => {
                    setFrequency(e.target.value as ScheduleFrequency);
                  }}
                  sx={{ minWidth: 120 }}
                  slotProps={{ htmlInput: { 'data-testid': 'schedule-frequency' } }}
                >
                  {FREQ_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  required
                  size="small"
                  inputMode="decimal"
                  label={sym === '' ? '每期金额' : `每期金额（${sym}）`}
                  placeholder={cents === null ? '0.00' : centsToYuanText(toCents(cents as number))}
                  value={amountText}
                  onChange={(e) => {
                    setAmountText(e.target.value);
                    setError(null);
                  }}
                  error={amountText !== '' && amountBad}
                  helperText="支出记正数；补贴等入账记负数"
                  sx={{ flex: 1 }}
                  slotProps={{ htmlInput: { 'data-testid': 'schedule-amount' } }}
                />
              </Stack>

              <TextField
                size="small"
                type="date"
                label={editing ? '下次到期日（留空 = 不改）' : '首次到期日'}
                value={nextRunAt}
                onChange={(e) => {
                  setNextRunAt(e.target.value);
                }}
                helperText={
                  editing
                    ? `原下次到期日：${rule.nextDueDay ?? '（没有下一期，此规则不会生成）'}`
                    : undefined
                }
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { 'data-testid': 'schedule-next-run' },
                }}
              />

              <CategoryPicker
                label="分类（可留空）"
                items={categories}
                kind="expense"
                value={categoryId}
                onChange={setCategoryId}
                showHint={false}
              />

              <TextField
                size="small"
                label="规则名称（可留空）"
                placeholder="如：房租 / 物业费"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                slotProps={{ htmlInput: { maxLength: 30 } }}
              />
            </>
          )}

          {error !== null && (
            <Alert severity="error" data-testid="schedule-error">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          取消
        </Button>
        <Button
          variant="contained"
          disabled={busy || amountBad || (!editing && (candidates.length === 0 || accountId === ''))}
          onClick={() => {
            setError(null);
            /*
             * 🔴 编辑态的 payload **只放 `UpdateScheduleRequest` 里有的字段**：
             * 不放 `enabled`（停用是列表行的开关，两个写入口会抢同一字段）、
             * 不放 `accountId` / `kind`（契约里没有）。
             * `categoryId` / `name` 一律显式传 —— 传 `null` = **清空**（这是用户可能的意图）。
             */
            if (editing) {
              update.mutate({
                amount: cents as number,
                frequency,
                categoryId,
                name: name.trim() === '' ? null : name.trim(),
                // 留空 ⇒ 不传 keys（服务端「未传 = 不改」）；**不能**传 null（契约是 `DayString.optional()`）
                ...(nextRunAt.trim() === '' ? {} : { nextRunAt }),
              });
              return;
            }
            create.mutate({
              accountId,
              kind,
              frequency,
              amount: cents as number,
              categoryId,
              nextRunAt,
              ...(name.trim() === '' ? {} : { name: name.trim() }),
            });
          }}
          data-testid="schedule-submit"
        >
          {busy ? '提交中…' : editing ? '保存修改' : '创建规则'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
